import {
  useTree,
  ScrollArea,
  Stack,
  Group,
  Title,
  SemiCircleProgress,
  Tree,
  Text,
} from "@mantine/core"
import {
  IconChevronDown,
  IconChevronUp,
  IconCircleCheck,
} from "@tabler/icons-react"
import _ from "lodash"

import { DigestResponses, TreeNode } from "@costory/types/endpoints/digest"

import { formatNumber } from "@costory/shared/utils/format"

const OTHER_NODE_LABEL = "Non drillable costs"
export const PreviewTree = ({
  data,
  columns,
}: {
  data: DigestResponses.GetTree
  columns: string[]
}) => {
  const { treeData, othersCost } = transformToTree(data, columns)
  const totalCost = data.filter((item) => item.dimensions.length === 0)[0].cost
  console.log(totalCost, othersCost)
  const percentageCostExplained = ((totalCost - othersCost) / totalCost) * 100
  const nbrNodes = data.filter((item) => item.cost > totalCost * 0.01).length

  return (
    <_PreviewTree
      data={data}
      treeData={treeData}
      percentageCostExplained={percentageCostExplained}
      nbrNodes={nbrNodes}
      totalCost={totalCost}
    />
  )
}

const _PreviewTree = ({
  data,
  treeData,
  percentageCostExplained,
  nbrNodes,
  totalCost,
}: {
  data: DigestResponses.GetTree
  treeData: TreeNode[]
  percentageCostExplained: number
  nbrNodes: number
  totalCost: number
}) => {
  const tree = useTree({
    initialExpandedState: data
      .filter((item) => item.cost > totalCost * 0.01)
      .reduce(
        (acc, item) => {
          acc[item.dimensions.map((d) => `${d.key}: ${d.value}`).join(",")] =
            true
          return acc
        },
        { Root: true } as Record<string, boolean>,
      ),
  })
  return (
    <ScrollArea h="50vh">
      <Stack gap="xl">
        <Group>
          <Stack>
            <Title order={6}>Analysis Quality Scorecard</Title>
            <Text>Based on cost last 90 days</Text>
            <Group>
              <SemiCircleProgress
                fillDirection="left-to-right"
                orientation="up"
                filledSegmentColor={
                  percentageCostExplained > 80
                    ? "success"
                    : percentageCostExplained > 50
                      ? "warning"
                      : "error"
                }
                size={200}
                thickness={12}
                value={percentageCostExplained}
                label={`${percentageCostExplained.toFixed(1)}% of cost explained`}
              />
              <Group justify="center" />
              <Stack justify="center">
                <Group>
                  <IconCircleCheck
                    color={percentageCostExplained >= 80 ? "green" : "red"}
                  />
                  <Text>
                    Cost Coverage: {percentageCostExplained.toFixed(1)}%
                    (target: &gt;80%)
                  </Text>
                </Group>
                <Group>
                  <IconCircleCheck
                    color={nbrNodes >= 5 && nbrNodes <= 20 ? "green" : "red"}
                  />
                  <Text>
                    Node Count: {nbrNodes} nodes (target between 5 and 20 nodes
                    with cost covering at least 1% of total cost)
                  </Text>
                </Group>
                <Group>
                  <IconCircleCheck
                    color={
                      Math.max(...data.map((d) => d.dimensions.length)) <= 5
                        ? "green"
                        : "red"
                    }
                  />
                  <Text>
                    Max Depth:{" "}
                    {Math.max(...data.map((d) => d.dimensions.length))} (target:
                    ≤5)
                  </Text>
                </Group>
              </Stack>
            </Group>
          </Stack>
        </Group>
        <Title order={6}>Cost Breakdown</Title>
        <Tree
          tree={tree}
          data={treeData}
          renderNode={({ node, expanded, hasChildren, elementProps }) => (
            <>
              <Group gap={5} {...elementProps} justify="space-between" w="100%">
                <Group gap={5}>
                  {hasChildren ? (
                    <IconChevronDown
                      size={18}
                      style={{
                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  ) : (
                    <IconChevronUp size={18} />
                  )}
                  {(node as { label: string }).label.startsWith(
                    OTHER_NODE_LABEL,
                  ) ? (
                    <Text c="error">{node.label}</Text>
                  ) : (
                    <Text>{node.label}</Text>
                  )}
                </Group>
                <Text
                  c={
                    (node as { label: string }).label?.startsWith(
                      OTHER_NODE_LABEL,
                    )
                      ? "error"
                      : "textPrimary"
                  }
                >
                  {formatNumber({
                    value: (node as unknown as { cost: number }).cost,
                    numberType: "currency",
                  })}{" "}
                </Text>
              </Group>
            </>
          )}
        />
      </Stack>
    </ScrollArea>
  )
}

const transformToTree = (data: DigestResponses.GetTree, columns: string[]) => {
  const orderedData = _.orderBy(data, "dimensions.length", "asc")
    .values()
    .toArray()
  const map = new Map<string, TreeNode>()
  orderedData.forEach((item) => {
    if (item.cost < 100) {
      return
    }
    const dimensions = item.dimensions
    const key = dimensions.map((d) => d.value).join(",")
    if (!map.has(key)) {
      map.set(key, {
        key,
        value:
          dimensions.length > 0
            ? `${dimensions[dimensions.length - 1].key}: ${dimensions[dimensions.length - 1].value}`
            : "Root",
        label:
          dimensions.length > 0
            ? dimensions[dimensions.length - 1].value
            : "Root",
        children: [],
        cost: item.cost,
        depth: dimensions.length,
      })
    }
    const parent = dimensions
      .slice(0, -1)
      .map((d) => d.value)
      .join(",")
    const parentNode = map.get(parent)
    if (parentNode && dimensions.length > 0) {
      parentNode.children.push(map.get(key)!)
    }
  })
  let othersCost = 0
  map.forEach((node) => {
    const costExplained = _.sumBy(node.children, (child) => child.cost)
    const costMissing = node.cost - costExplained
    if (node.depth < columns.length && costMissing > 100) {
      const othersNode = {
        key: node.key + ",Others",
        value: node.key + ",Others",
        label: "Non drillable costs",
        children: [],
        cost: costMissing,
        depth: node.depth + 1,
      }
      node.children.push(othersNode)
      othersCost += costMissing
    }
  })
  // last loop to create the correct label:
  map.forEach((node) => {
    node.label = `${node.label}`
  })
  // reorder every child by descending cost
  map.forEach((node) => {
    node.children = _.orderBy(node.children, "cost", "desc")
  })
  const root = map.get("")
  if (!root) {
    return { treeData: [], othersCost }
  }
  return { treeData: [root], othersCost }
}
